import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { NumberCircle } from '~/components/common/NumberCircle'
import { snackActions } from '~/lib/snackbarHelper'
import React, { memo, useMemo, useState } from 'react'
import { Controller, FormProvider, useFormContext } from 'react-hook-form'
import { ReservableTimes } from '~/lib/feature/reservation'
import { type TableComponents as TableComponentsType, TableVirtuoso } from 'react-virtuoso'
import { DateTime } from 'luxon'
import { WeekDay } from '~/generated/graphql'
import { SimpleDialog } from '~/components/common/SimpleDialog'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

type Props = {
  name: string // react-hook-formのname
  disabled?: boolean
  showAlert?: boolean
}

export const daysOfWeek = [
  { name: '月', key: WeekDay.Monday },
  { name: '火', key: WeekDay.Tuesday },
  { name: '水', key: WeekDay.Wednesday },
  { name: '木', key: WeekDay.Thursday },
  { name: '金', key: WeekDay.Friday },
  { name: '土', key: WeekDay.Saturday },
  { name: '日', key: WeekDay.Sunday },
] as const

export type DayOfWeek = (typeof WeekDay)[keyof typeof WeekDay]

/**
 * @see https://github.com/petyosi/react-virtuoso/issues/1045
 */
const TableComponents = {
  Scroller: TableContainer,
  Table: (props) => <Table {...props} style={{ borderCollapse: 'separate' }} />,
  TableHead: TableHead,
  TableRow: TableRow,
  TableBody: TableBody,
} satisfies TableComponentsType

/**
 * react-hook-formのFormContextの中で使う
 * FieldValuesの型は{[name]: {[key: string]: boolean}}となる
 * keyは「YYYY-MM-DD H:mm」の形式
 */
export const CampaignPublishSchedulePicker = memo<Props>(({ name, disabled, showAlert }) => {
  const { formState } = useFormContext()
  const error = formState.errors.schedules?.message

  return (
    <Stack gap={2} sx={{ maxWidth: '90vw', height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <BulkPickButton name={name} disabled={disabled} showAlert={showAlert} />
      </Stack>
      <TableVirtuoso
        initialTopMostItemIndex={9 * 2}
        style={{ height: '600px', overflowX: 'auto' }}
        data={ReservableTimes}
        components={TableComponents}
        fixedHeaderContent={() => (
          <TableRow>
            <TableCell
              component="th"
              size="small"
              sx={{
                textAlign: 'end',
                fontWeight: 'bold',
                borderRight: (theme) => '1px solid ' + theme.palette.divider,
                backgroundColor: 'white',
              }}
            >
              {/* 仮想スクロールによって新しく描画されたときに幅がずれないようにスペーサーとして不可視で実際の内容を描画する */}
              <Box sx={{ visibility: 'hidden' }}>23:00</Box>
            </TableCell>
            {daysOfWeek.map(({ name: dayName, key }) => (
              <TableCell
                key={key}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  backgroundColor: key === WeekDay.Sunday ? '#FFF2F2' : key === WeekDay.Saturday ? '#EBF5FF' : 'white',
                  color: key === WeekDay.Sunday ? '#A90000' : undefined,
                }}
              >
                {dayName}
              </TableCell>
            ))}
          </TableRow>
        )}
        itemContent={(_, time) => (
          <>
            <TableCell
              component="th"
              size="small"
              sx={{
                textAlign: 'end',
                fontWeight: 'bold',
                border: 'none',
                borderRight: (theme) => '1px solid ' + theme.palette.divider,
                backgroundColor: 'white',
              }}
            >
              {time}
            </TableCell>
            {daysOfWeek.map((day) => (
              <TableCell
                key={day.key}
                size="small"
                sx={{
                  border: 'none',
                  textAlign: 'center',
                  backgroundColor:
                    day.key === WeekDay.Sunday ? '#FFF2F2' : day.key === WeekDay.Saturday ? '#EBF5FF' : 'white',
                  padding: 0,
                }}
              >
                <CheckboxController name={name} time={time} day={day} />
              </TableCell>
            ))}
          </>
        )}
      />
      {typeof error === 'string' && <ValidationErrorMessage error={error} sx={{ mt: 1 }} />}
    </Stack>
  )
})
CampaignPublishSchedulePicker.displayName = 'CampaignPublishSchedulePicker'

// FormProviderで渡される参照が安定ではなく、useFormContextがメモ化を貫通して再描画させてしまうのでcontrolを取り出してメモ化する
// これを行わないと<FormProvider>を含むコンポーネントが再描画されるたびに数百個のチェックボックスが再描画されてしまう
// note: useFormContextが再描画をトリガーするので、小さいコンポーネントに切り出して呼び出す
//       実際のuseFormの返り値と、その中のcontrolは基本的に同じオブジェクトが使われ続ける
const CheckboxController = ({ name, time, day }: { name: string; time: string; day: (typeof daysOfWeek)[number] }) => {
  const { control } = useFormContext()

  return useMemo(
    () => (
      // Controller内のuseFormContextが外側のFormProviderを読み取ってしまうので、無効化するために空のFormProviderを挟む
      // @ts-expect-error note: 想定された使い方ではないので更新時に壊れないか気を付ける
      <FormProvider {...null}>
        <Controller
          control={control}
          name={name + '.' + day.key + '_' + time}
          defaultValue={false}
          render={() => (
            <Controller
              control={control}
              name={name + '.' + day.key + '_' + time}
              defaultValue={false}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Tooltip title={`${day.name}曜日 ${time}`} disableInteractive>
                  <Checkbox onBlur={onBlur} onChange={onChange} checked={value} inputRef={ref} />
                </Tooltip>
              )}
            />
          )}
        />
      </FormProvider>
    ),
    [control, day.key, day.name, name, time],
  )
}

const BulkPickButton = ({ name, disabled, showAlert }: { name: string; disabled?: boolean; showAlert?: boolean }) => {
  const { setValue } = useFormContext()
  const [openBulkEditDialog, setOpenBulkEditDialog] = useState(false)

  return (
    <>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Button variant="outlined" size="large" onClick={() => setOpenBulkEditDialog(true)} disabled={disabled}>
          一括指定
        </Button>
        <Typography variant="body2" color="text.secondary">
          曜日・時間でまとめて指定できます。
        </Typography>
      </Stack>
      <BulkPickDialog
        open={openBulkEditDialog}
        onClose={() => setOpenBulkEditDialog(false)}
        onOk={(data) => {
          Object.entries(data).forEach(([key, value]) => {
            setValue(name + '.' + key, value)
          })
          setOpenBulkEditDialog(false)
        }}
        showAlert={showAlert}
      />
    </>
  )
}

const BulkPickDialog = ({
  open,
  onClose,
  onOk,
  showAlert,
}: {
  open: boolean
  onClose: () => void
  onOk: (data: { [key: string]: boolean }) => void
  showAlert?: boolean
}) => {
  const [checked, setChecked] = useState(true)
  const [days, setDays] = useState<DayOfWeek[]>([])
  const [startTime, setStartTime] = useState('9:00')
  const [endTime, setEndTime] = useState('18:00')
  const timeError = DateTime.fromFormat(endTime, 'H:mm') < DateTime.fromFormat(startTime, 'H:mm')

  const parseTime = (time: string) =>
    Number.parseInt(time.split(':')[0] || '') * 60 + Number.parseInt(time.split(':')[1] || '')

  const startMinuteValue = parseTime(startTime)
  const endMinuteValue = parseTime(endTime)

  const reset = () => {
    setChecked(true)
    setDays([])
    setStartTime('9:00')
    setEndTime('18:00')
  }

  const handleOk = ({ showAlert }: { showAlert?: boolean }) => {
    const data: { [key: string]: boolean } = {}
    daysOfWeek.forEach(({ key }) => {
      if (days.includes(key)) {
        ReservableTimes.forEach((time) => {
          const timeMinuteValue = parseTime(time)

          if (startMinuteValue <= timeMinuteValue && timeMinuteValue <= endMinuteValue) {
            data[key + '_' + time] = checked
          }
        })
      }
    })
    onOk(data)
    reset()
    snackActions.info(`一括指定しました。${showAlert ? '保存するには「保存する」ボタンを押してください。' : ''}`)
  }

  const handleClose = () => {
    onClose()
    reset()
  }

  return (
    <SimpleDialog
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onOk={() => handleOk({ showAlert })}
      title="一括指定"
      okButtonText="一括指定"
      closeButtonText="閉じる"
      disabledOkButton={timeError || days.length === 0}
      size="md"
      mobileFullScreen
    >
      <Stack gap={2}>
        <Typography>
          <NumberCircle>1</NumberCircle>
          <Typography component="span" ml={2}>
            来店可否を指定してください。
          </Typography>
        </Typography>
        <RadioGroup sx={{ ml: 5 }} value={checked.toString()} onChange={(e) => setChecked(e.target.value === 'true')}>
          <FormControlLabel value="true" control={<Radio />} label="来店可" />
          <FormControlLabel value="false" control={<Radio />} label="来店不可" />
        </RadioGroup>
        <Typography>
          <NumberCircle>2</NumberCircle>
          <Typography component="span" ml={2}>
            曜日を指定してください。
          </Typography>
        </Typography>
        <Stack direction="row" gap={2} ml={5} flexWrap="wrap">
          {daysOfWeek.map(({ name: dayName, key }) => {
            return (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={days.includes(key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDays((days) => [...days, key])
                      } else {
                        setDays((days) => days.filter((d) => d !== key))
                      }
                    }}
                  />
                }
                label={dayName}
              />
            )
          })}
        </Stack>
        <Typography>
          <NumberCircle>3</NumberCircle>
          <Typography component="span" ml={2}>
            時間を指定してください。
          </Typography>
        </Typography>
        <Stack direction="row" gap={2} ml={5}>
          <Select
            sx={{ width: '200px' }}
            error={timeError}
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value as string)
            }}
          >
            {ReservableTimes.map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>
          <Typography
            sx={{
              alignSelf: 'center',
            }}
          >
            〜
          </Typography>
          <Select
            sx={{ width: '200px' }}
            error={timeError}
            value={endTime}
            onChange={(e) => {
              setEndTime(e.target.value as string)
            }}
          >
            {ReservableTimes.map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        {timeError && <ValidationErrorMessage error="終了時間は開始時間よりも後に設定してください。" sx={{ ml: 5 }} />}
        {showAlert && (
          <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
            「一括指定」を行うだけでは、編集結果は反映されません。{'\n'}
            ダイアログが閉じた後、「保存する」ボタンを押してください。
          </Alert>
        )}
      </Stack>
    </SimpleDialog>
  )
}
